/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { quarterlist } from "../../../utils/constant";
import styles from "../../../styles/governance/dashboard.module.css";
import Cards from "../../../Components/Governance/Cards";
import LineChart from "../../../Components/GraphNCharts/LineChart";
import { BarChart as MuiBarChart } from "@mui/x-charts/BarChart";
import { CircularProgress } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import { get } from "../../../utils/api";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import Unauthorized from "../../../Components/Unauthorized/Unauthorized";
import { fixedPercentage, truncateNumber } from "../../../utils/common";
import { useUser } from "../../../context/AuthContext";
import GroupAdminSidebar from "../../../Layout/GroupAdminSidebar";
import useAuthStore from "../../../store/ContextUser";
import GovernanceTickIcon from "../../../assets/Governance/GovernanceTickIcon.svg";
import GovernanceCrossIcon from "../../../assets/Governance/GovernanceCrossIcon.svg";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const Dashboard = () => {
  const { user } = useUser();
  const { selectedCompany } = useAuthStore();
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const getQuarterValue = (month) => {
    if (month >= 1 && month <= 3) return "1";
    if (month >= 4 && month <= 6) return "2";
    if (month >= 7 && month <= 9) return "3";
    return "4";
  };

  const [viewType, setViewType] = useState("quarterly");
  const [fromQuarter, setFromQuarter] = useState("1");
  const [toQuarter, setToQuarter] = useState(getQuarterValue(currentMonth));
  const [fromYear, setFromYear] = useState(currentDate.getFullYear());
  const [toYear, setToYear] = useState(currentDate.getFullYear());
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [governanceData, setGovernanceData] = useState({
    board: {},
    executive: {},
    risk: {},
    compliance: {},
    transparency: {},
  });

  const yearList = useMemo(() => {
    const startYear = 2021;
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: currentYear - startYear + 1 },
      (_, i) => startYear + i,
    );
  }, []);

  const availablePeriods = useMemo(() => {
    const raw = governanceData?.byQuery?.data ?? governanceData?.byQuery;
    const fallback = {
      years: yearList,
      quartersByYear: Object.fromEntries(
        yearList.map((y) => [String(y), [1, 2, 3, 4]]),
      ),
    };

    if (!raw) return fallback;

    // Accept multiple possible shapes:
    // 1) { years: [2024,2025], quartersByYear: { "2024":[1,2], "2025":[1,4] } }
    // 2) { data: [{ year: 2024, quarter: 1 }, ...] } or [{ year, quarter }]
    // 3) { years: [...], quarters: [...] } (quarters assumed available for all years)
    const yearsFromRaw =
      raw?.years ?? raw?.data?.years ?? (Array.isArray(raw) ? null : null);

    const quartersByYearFromRaw =
      raw?.quartersByYear ?? raw?.data?.quartersByYear ?? null;

    if (
      Array.isArray(yearsFromRaw) &&
      quartersByYearFromRaw &&
      typeof quartersByYearFromRaw === "object"
    ) {
      const years = yearsFromRaw
        .map((y) => Number(y))
        .filter(Boolean)
        .sort((a, b) => a - b);
      const quartersByYear = {};
      years.forEach((y) => {
        const q = quartersByYearFromRaw[String(y)] ??
          quartersByYearFromRaw[y] ?? [1, 2, 3, 4];
        quartersByYear[String(y)] = (Array.isArray(q) ? q : [q])
          .map((v) => Number(v))
          .filter((v) => v >= 1 && v <= 4)
          .sort((a, b) => a - b);
      });
      return {
        years: years.length ? years : fallback.years,
        quartersByYear: Object.keys(quartersByYear).length
          ? quartersByYear
          : fallback.quartersByYear,
      };
    }

    const rows = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : null;
    if (Array.isArray(rows)) {
      const quartersByYear = {};
      rows.forEach((r) => {
        const y = r?.year ?? r?.startyear ?? r?.startYear ?? r?.Year;
        const q =
          r?.quarter ?? r?.startquarter ?? r?.startQuarter ?? r?.Quarter;
        const yNum = Number(y);
        const qNum = Number(q);
        if (!yNum || !(qNum >= 1 && qNum <= 4)) return;
        const key = String(yNum);
        if (!quartersByYear[key]) quartersByYear[key] = new Set();
        quartersByYear[key].add(qNum);
      });
      const years = Object.keys(quartersByYear)
        .map((y) => Number(y))
        .filter(Boolean)
        .sort((a, b) => a - b);
      const normalized = {};
      years.forEach((y) => {
        normalized[String(y)] = Array.from(quartersByYear[String(y)] || [])
          .map(Number)
          .filter((v) => v >= 1 && v <= 4)
          .sort((a, b) => a - b);
      });
      if (years.length) {
        return { years, quartersByYear: normalized };
      }
    }

    if (Array.isArray(raw?.years)) {
      const years = raw.years
        .map((y) => Number(y))
        .filter(Boolean)
        .sort((a, b) => a - b);
      const quarters = Array.isArray(raw?.quarters)
        ? raw.quarters.map((q) => Number(q)).filter((q) => q >= 1 && q <= 4)
        : [1, 2, 3, 4];
      const quartersByYear = Object.fromEntries(
        years.map((y) => [String(y), quarters]),
      );
      return { years: years.length ? years : fallback.years, quartersByYear };
    }

    return fallback;
  }, [governanceData?.byQuery, yearList]);

  const availableYears = availablePeriods.years;
  const availableQuartersByYear = availablePeriods.quartersByYear;

  const fromYearOptions = useMemo(() => {
    if (viewType === "annually") return availableYears;
    // For quarterly, only show years that have at least 1 quarter available.
    return availableYears.filter(
      (y) => (availableQuartersByYear[String(y)] || []).length > 0,
    );
  }, [availableYears, availableQuartersByYear, viewType]);

  const fromQuarterOptions = useMemo(() => {
    if (viewType !== "quarterly") return quarterlist;
    const qs = availableQuartersByYear[String(fromYear)] || [];
    const allowed = qs.length ? qs : [1, 2, 3, 4];
    return quarterlist.filter((q) => allowed.includes(Number(q.value)));
  }, [viewType, fromYear, availableQuartersByYear]);

  const toYearOptions = useMemo(() => {
    const fromYearNum = Number(fromYear);
    if (!fromYearNum) return fromYearOptions;

    // Allow same year in both quarterly and annual modes.
    return fromYearOptions.filter((year) => Number(year) >= fromYearNum);
  }, [fromYearOptions, fromYear, viewType]);

  const toQuarterOptions = useMemo(() => {
    const fromYearNum = Number(fromYear);
    const toYearNum = Number(toYear);
    const fromQuarterNum = Number(fromQuarter);

    if (!fromYearNum || !toYearNum || !fromQuarterNum)
      return fromQuarterOptions;

    const toYearAllowed = availableQuartersByYear[String(toYearNum)] || [];
    const base = toYearAllowed.length
      ? quarterlist.filter((q) => toYearAllowed.includes(Number(q.value)))
      : fromQuarterOptions;

    if (toYearNum > fromYearNum) return base;
    if (toYearNum < fromYearNum) return [];
    // Allow selecting the same quarter in "To" as in "From"
    return base.filter((quarter) => Number(quarter.value) >= fromQuarterNum);
  }, [
    fromYear,
    toYear,
    fromQuarter,
    fromQuarterOptions,
    availableQuartersByYear,
  ]);

  useEffect(() => {
    const isValidToYear = toYearOptions.some(
      (year) => String(year) === String(toYear),
    );
    if (!isValidToYear && toYearOptions.length > 0) {
      setToYear(toYearOptions[0]);
    }
  }, [toYear, toYearOptions]);

  useEffect(() => {
    const isValidFromYear = fromYearOptions.some(
      (year) => String(year) === String(fromYear),
    );
    if (!isValidFromYear && fromYearOptions.length > 0) {
      setFromYear(fromYearOptions[0]);
    }
  }, [fromYear, fromYearOptions]);

  useEffect(() => {
    if (viewType !== "quarterly") return;
    const isValidFromQuarter = fromQuarterOptions.some(
      (q) => String(q.value) === String(fromQuarter),
    );
    if (!isValidFromQuarter && fromQuarterOptions.length > 0) {
      setFromQuarter(String(fromQuarterOptions[0].value));
    }
  }, [viewType, fromQuarter, fromQuarterOptions]);

  useEffect(() => {
    if (viewType !== "quarterly") return;

    if (toQuarterOptions.length === 0) {
      if (Number(toYear) === Number(fromYear)) {
        const nextYear = yearList.find(
          (year) => Number(year) > Number(fromYear),
        );
        if (nextYear) setToYear(nextYear);
      }
      return;
    }

    const isValidToQuarter = toQuarterOptions.some(
      (quarter) => String(quarter.value) === String(toQuarter),
    );
    if (!isValidToQuarter) {
      setToQuarter(String(toQuarterOptions[0].value));
    }
  }, [viewType, toQuarterOptions, toQuarter, fromYear, toYear, yearList]);

  const buildPeriodParams = () => {
    // Backend expects quarter+year always; for annual we send full-year range (Q1..Q4).
    if (viewType === "annually") {
      return `startquarter=1&startyear=${fromYear}&endquarter=4&endyear=${toYear}`;
    }
    return `startquarter=${fromQuarter}&startyear=${fromYear}&endquarter=${toQuarter}&endyear=${toYear}`;
  };

  const fetchGovernanceData = async () => {
    try {
      setIsLoading(true);
      const periodParams = buildPeriodParams();
      const companyId = selectedCompany?._id;

      // Single combined endpoint (preferred)
      const modules = [
        "board",
        "executive",
        "risk",
        "compliance",
        "transparency",
      ];
      const combinedUrl = `governance/dashboard/combined?${periodParams}&modules=${modules.join(
        ",",
      )}&companyId=${companyId}`;

      try {
        const res = await get(combinedUrl);
        const payload = res?.data?.data ?? res?.data;
        // Expecting shape: { board: {...}, executive: {...}, risk: {...}, compliance: {...}, transparency: {...} }
        if (payload && typeof payload === "object") {
          setGovernanceData((prev) => ({
            ...prev,
            ...payload,
          }));
          return;
        }
      } catch (e) {
        // If backend isn't ready for combined endpoint, fallback to legacy calls below.
        console.warn(
          "Combined governance dashboard API failed; falling back.",
          e,
        );
      }

      // Legacy multi-endpoint fallback
      const endpoints = {
        // board: `governance/dashboard/boardStructureAndComposition?${periodParams}&companyId=${companyId}`,
        // executive: `governance/dashboard/executiveCompensation?${periodParams}&companyId=${companyId}`,
        // risk: `governance/dashboard/riskManangement?${periodParams}&companyId=${companyId}`,
        // compliance: `governance/dashboard/complianceAndEthics?${periodParams}&companyId=${companyId}`,
        // transparency: `governance/dashboard/transparencyAndDisclosure?${periodParams}&companyId=${companyId}`,
        // byQuery:`governance/dashboard/byQuery?${periodParams}&companyId=${companyId}`,
        // Only fetch byQuery here (used to build available From/To options).
        byQuery: `governance/dashboard/byQuery?${periodParams}&module=${viewType}`,
      };

      const responses = await Promise.all(
        Object.entries(endpoints).map(async ([key, url]) => {
          const res = await get(url);
          return { [key]: res.data };
        }),
      );
      const combinedData = responses.reduce(
        (acc, data) => ({ ...acc, ...data }),
        {},
      );
      // Keep existing dashboard data; update only byQuery availability payload.
      setGovernanceData((prev) => ({
        ...prev,
        ...combinedData,
      }));
    } catch (error) {
      console.error("Error fetching governance data:", error);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };
  console.log(governanceData, "governanceData");

  useEffect(() => {
    fetchGovernanceData();
  }, [
    viewType,
    fromQuarter,
    toQuarter,
    fromYear,
    toYear,
    selectedCompany,
    user?._id,
  ]);

  const getChangeType = (percentage) =>
    percentage > 0 ? "increase" : "decrease";

  const parseValue = (value) => {
    if (value === null || value === undefined) return 0;
    const parsed = parseFloat(String(value).replace("%", ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  const truncateToTwoDecimals = (value) => {
    const num = Number(value) || 0;
    return Math.trunc(num * 100) / 100;
  };
  const formatTwoDigitsIfDecimal = (value) => {
    const num = Number(value) || 0;
    return Number.isInteger(num)
      ? truncateNumber(num, 0)
      : truncateNumber(num, 2);
  };

  const quarterTimelineLabels = useMemo(() => {
    const selectedQuarter = Number(toQuarter) || 1;
    const selectedYear = Number(toYear) || new Date().getFullYear();

    if (viewType === "annually") {
      return [1, 2, 3, 4].map((q) => `Q${q} ${selectedYear}`);
    }

    const labels = [];
    let q = selectedQuarter;
    let y = selectedYear;

    for (let i = 0; i < 4; i += 1) {
      labels.unshift(`Q${q} ${y}`);
      q -= 1;
      if (q === 0) {
        q = 4;
        y -= 1;
      }
    }

    return labels;
  }, [toQuarter, toYear, viewType]);

  const selectedQuarterRangeLabels = useMemo(() => {
    if (viewType !== "quarterly") return quarterTimelineLabels;

    const startYear = Number(fromYear);
    const endYear = Number(toYear);
    const startQuarter = Number(fromQuarter);
    const endQuarter = Number(toQuarter);

    if (
      !startYear ||
      !endYear ||
      !startQuarter ||
      !endQuarter ||
      endYear < startYear
    ) {
      return quarterTimelineLabels;
    }

    const labels = [];
    let y = startYear;
    let q = startQuarter;

    while (y < endYear || (y === endYear && q <= endQuarter)) {
      labels.push(`Q${q} ${y}`);
      q += 1;
      if (q === 5) {
        q = 1;
        y += 1;
      }
      if (labels.length > 64) break; // Safety guard for unexpected loops.
    }

    return labels.length ? labels : quarterTimelineLabels;
  }, [
    viewType,
    fromYear,
    toYear,
    fromQuarter,
    toQuarter,
    quarterTimelineLabels,
  ]);

  const parseQuarterNumber = (q) => {
    const raw = String(q ?? "").trim();
    if (!raw) return null;
    const asNum = Number(raw);
    if (asNum >= 1 && asNum <= 4) return asNum;
    const normalized = raw.toLowerCase().replace(/\s+/g, "");
    if (
      normalized === "jan-mar" ||
      normalized === "jan–mar" ||
      normalized === "jan—mar" ||
      normalized === "q1"
    )
      return 1;
    if (
      normalized === "apr-jun" ||
      normalized === "apr–jun" ||
      normalized === "apr—jun" ||
      normalized === "q2"
    )
      return 2;
    if (
      normalized === "jul-sep" ||
      normalized === "jul–sep" ||
      normalized === "jul—sep" ||
      normalized === "q3"
    )
      return 3;
    if (
      normalized === "oct-dec" ||
      normalized === "oct–dec" ||
      normalized === "oct—dec" ||
      normalized === "q4"
    )
      return 4;
    return null;
  };

  const selectedQuarterRows = useMemo(() => {
    const byQueryPayload = governanceData?.byQuery || {};
    const quarters = Array.isArray(byQueryPayload?.quarters)
      ? byQueryPayload.quarters
      : [];

    const rowByLabel = {};
    quarters.forEach((row) => {
      const year = Number(
        row?.year ??
          row?.startyear ??
          row?.startYear ??
          row?.endyear ??
          row?.endYear,
      );
      const quarterNum = parseQuarterNumber(
        row?.quarter ??
          row?.startquarter ??
          row?.startQuarter ??
          row?.endquarter ??
          row?.endQuarter,
      );
      if (!year || !quarterNum) return;
      rowByLabel[`Q${quarterNum} ${year}`] = row;
    });

    return selectedQuarterRangeLabels.map((label) => {
      const match = String(label).match(/^Q([1-4])\s+(\d{4})$/);
      const quarterNum = match ? Number(match[1]) : null;
      const year = match ? Number(match[2]) : null;
      return {
        label,
        quarterNum,
        year,
        row: rowByLabel[label] || null,
      };
    });
  }, [governanceData?.byQuery, selectedQuarterRangeLabels]);

  const boardSummary = useMemo(() => {
    const byQueryPayload = governanceData?.byQuery || {};
    const normalizedQuarters = selectedQuarterRows.map((entry) => {
      const row = entry?.row || {};
      return {
        year: entry?.year,
        quarterNum: entry?.quarterNum,
        totalBoardMembers: parseValue(row?.totalBoardMembers),
        femaleBoardMembers: parseValue(
          row?.data?.boardStructure?.femaleBoardMembers,
        ),
        womenPercentage: parseValue(row?.womenPercentage),
        independentPercentage: parseValue(
          row?.independentPercentage ??
            row?.data?.boardStructure?.independentPercentage ??
            row?.data?.boardStructure?.independentDirectorsPercentage,
        ),
        independentBoardMembers: parseValue(
          row?.independentBoardMembers ??
            row?.independentDirectors ??
            row?.data?.boardStructure?.independentBoardMembers ??
            row?.data?.boardStructure?.independentDirectors,
        ),
        averageTenure: parseValue(
          row?.averageTenure ??
            row?.data?.boardStructure?.averageBoardTenure ??
            row?.data?.boardStructure?.averageTenure,
        ),
      };
    });
    const latest = normalizedQuarters[normalizedQuarters.length - 1];
    const latestTotal = Number(latest?.totalBoardMembers) || 0;
    const latestFemale = Number(latest?.femaleBoardMembers) || 0;
    const latestWomenPct =
      latestTotal > 0 ? (latestFemale / latestTotal) * 100 : 0;
    const rangeTotals = normalizedQuarters.reduce(
      (acc, row) => {
        acc.total += Number(row?.totalBoardMembers) || 0;
        acc.female += Number(row?.femaleBoardMembers) || 0;
        const total = Number(row?.totalBoardMembers) || 0;
        const directIndependentCount = Number(row?.independentBoardMembers);
        if (!Number.isNaN(directIndependentCount)) {
          acc.independent += Math.max(directIndependentCount, 0);
        } else {
          const rowIndependentPct = Number(row?.independentPercentage);
          if (!Number.isNaN(rowIndependentPct) && rowIndependentPct >= 0) {
            acc.independent += (total * rowIndependentPct) / 100;
          }
        }
        return acc;
      },
      { total: 0, female: 0, independent: 0 },
    );
    const rangeWomenPct =
      rangeTotals.total > 0
        ? (rangeTotals.female / rangeTotals.total) * 100
        : 0;
    const rangeIndependentPct =
      rangeTotals.total > 0
        ? (rangeTotals.independent / rangeTotals.total) * 100
        : 0;
    const avgTenureAcrossSelectedQuarters = normalizedQuarters.length
      ? normalizedQuarters.reduce(
          (sum, row) => sum + (Number(row?.averageTenure) || 0),
          0,
        ) / normalizedQuarters.length
      : 0;

    return {
      totalBoardMembers:
        parseValue(byQueryPayload?.totalBoardMembers) ||
        latest?.totalBoardMembers ||
        0,
      womenPercentage:
        rangeWomenPct || latest?.womenPercentage || latestWomenPct || 0,
      independentPercentage:
        rangeIndependentPct || latest?.independentPercentage || 0,
      averageTenure:
        avgTenureAcrossSelectedQuarters || latest?.averageTenure || 0,
      quarterLabels: normalizedQuarters.map(
        (r) => `Q${r.quarterNum} ${r.year}`,
      ),
      totalMemberTrend: normalizedQuarters.map((r) =>
        truncateNumber(r.totalBoardMembers, 2),
      ),
      womenCounts: normalizedQuarters.map((r) => {
        const total = Number(r.totalBoardMembers) || 0;
        const female = Number(r.femaleBoardMembers) || 0;
        // Prefer direct female count from backend.
        if (female > 0 || total === 0) return truncateNumber(female, 0);
        // Fallback to percentage if backend doesn't send female count.
        return truncateNumber(
          (total * (Number(r.womenPercentage) || 0)) / 100 || 0,
          0,
        );
      }),
      menCounts: normalizedQuarters.map((r) => {
        const total = Number(r.totalBoardMembers) || 0;
        const female = Number(r.femaleBoardMembers) || 0;
        if (total > 0) return truncateNumber(Math.max(total - female, 0), 0);
        return 0;
      }),
    };
  }, [governanceData?.byQuery, selectedQuarterRows]);

  const quarterlyComplianceViolationsTotal = useMemo(() => {
    const values = selectedQuarterRows
      .map((entry) => {
        const row = entry?.row || {};
        const v =
          row?.complianceAndEthics?.complianceViolations ??
          row?.data?.complianceAndEthics?.complianceViolations ??
          row?.data?.complianceAndEthics?.complianceViolationsCount ??
          row?.data?.complianceAndEthics?.violations ??
          row?.data?.complianceViolations ??
          0;
        return Number(v) || 0;
      })
      .filter((v) => !Number.isNaN(v));

    if (!values.length) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }, [selectedQuarterRows]);

  const quarterlyComplianceViolationsYoY = useMemo(() => {
    const rows = selectedQuarterRows
      .map((entry) => {
        const row = entry?.row || {};
        const v =
          row?.complianceAndEthics?.complianceViolations ??
          row?.data?.complianceAndEthics?.complianceViolations ??
          row?.data?.complianceAndEthics?.complianceViolationsCount ??
          row?.data?.complianceAndEthics?.violations ??
          row?.data?.complianceViolations ??
          0;
        return {
          year: entry?.year,
          quarterNum: entry?.quarterNum,
          value: Number(v) || 0,
        };
      })
      .filter((r) => r.year && r.quarterNum);

    if (rows.length < 2) return null;

    const sorted = rows.sort(
      (a, b) => a.year - b.year || a.quarterNum - b.quarterNum,
    );
    const current = sorted[sorted.length - 1]?.value ?? null;
    const previous = sorted[sorted.length - 2]?.value ?? null;

    if (previous === null || previous === 0 || current === null) return null;
    return ((current - previous) / previous) * 100;
  }, [selectedQuarterRows]);

  const quarterlyFinePaidSeries = useMemo(() => {
    const rows = selectedQuarterRows
      .map((entry) => {
        const row = entry?.row || {};
        console.log("row", row);
        const v =
          row?.complianceAndEthics?.totalFinesAndPenalties?.amount ??
          row?.data?.complianceAndEthics?.totalFinesAndPenalties?.amount ??
          row?.data?.complianceAndEthics?.totalFinesAndPenalties?.amount ??
          row?.data?.totalFinesAndPenalties?.amount ??
          0;
        return {
          year: entry?.year,
          quarterNum: entry?.quarterNum,
          // Backend amount is stored as absolute currency value; convert to Lakhs for UI/chart.
          value: (Number(v) || 0) / 100000,
        };
      })
      .filter((r) => r.year && r.quarterNum)
      .sort((a, b) => a.year - b.year || a.quarterNum - b.quarterNum);

    const labels = rows.map((r) => `Q${r.quarterNum} ${r.year}`);
    const values = rows.map((r) => r.value);
    const total = values.reduce((s, n) => s + (Number(n) || 0), 0);
    const max = values.length ? Math.max(...values) : 0;

    return { labels, values, total, max };
  }, [selectedQuarterRows]);

  const quarterlyFinePaidDisplaySeries = useMemo(() => {
    const labels = Array.isArray(quarterlyFinePaidSeries.labels)
      ? quarterlyFinePaidSeries.labels
      : [];
    const rawValues = Array.isArray(quarterlyFinePaidSeries.values)
      ? quarterlyFinePaidSeries.values
      : [];
    const values = rawValues.map((v) => Number(v) || 0);

    return {
      labels,
      values,
      max: values.length ? Math.max(...values) : 0,
    };
  }, [quarterlyFinePaidSeries]);

  const quarterlyFineXAxisConfig = useMemo(() => {
    const dataMax = Number(quarterlyFinePaidDisplaySeries.max) || 0;

    // Requested behavior for small Lakhs values:
    // if highest is around 0.2, end axis at 0.5; otherwise use 1.
    if (dataMax <= 0.2) {
      return { max: 0.5, stepSize: 0.1 };
    }
    if (dataMax <= 1) {
      return { max: 1, stepSize: 0.2 };
    }

    // For larger values keep a rounded dynamic scale.
    const rawMax = dataMax * 1.15;
    const roughStep = rawMax / 5;
    const pow = 10 ** Math.floor(Math.log10(roughStep || 1));
    const norm = roughStep / pow;
    const niceBase = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
    const stepSize = niceBase * pow;
    const max = Math.ceil(rawMax / stepSize) * stepSize;
    return { max, stepSize };
  }, [quarterlyFinePaidDisplaySeries.max]);

  const shareholderVotingSummary = useMemo(() => {
    const parsePct = (v) => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      if (!s) return null;
      const n = Number(s.replace("%", ""));
      return Number.isNaN(n) ? null : n;
    };

    const rows = selectedQuarterRows
      .map((entry) => {
        const row = entry?.row || {};

        const perf =
          row?.shareholderRights?.shareholderVotingParticipationRate
            ?.performance ??
          row?.data?.shareholderRights?.shareholderVotingParticipationRate
            ?.performance ??
          null;

        return {
          year: entry?.year,
          quarterNum: entry?.quarterNum,
          performance: parsePct(perf),
        };
      })
      .filter((r) => r.year && r.quarterNum && r.performance !== null)
      .sort((a, b) => a.year - b.year || a.quarterNum - b.quarterNum);

    if (rows.length === 0) return { value: null, changePct: null };

    const avg = (list) => {
      const vals = list
        .map((r) => Number(r.performance))
        .filter((v) => !Number.isNaN(v));
      if (!vals.length) return null;
      return vals.reduce((s, v) => s + v, 0) / vals.length;
    };

    // Dashboard value: average of selected range
    // (performance1 + performance2 + ... ) / numberOfQuarters
    const value = avg(rows);

    // QOQ change from latest selected quarter vs previous selected quarter
    const latestRow = rows[rows.length - 1];
    const prevRow = rows.length > 1 ? rows[rows.length - 2] : null;
    const changePct =
      !prevRow || !prevRow.performance || latestRow?.performance === null
        ? null
        : ((latestRow.performance - prevRow.performance) /
            prevRow.performance) *
          100;

    return { value, changePct };
  }, [selectedQuarterRows]);

  console.log(shareholderVotingSummary, "changePct++");
  const womenBoardSeries = useMemo(
    () => ({
      women: boardSummary.womenCounts || [],
      men: boardSummary.menCounts || [],
    }),
    [boardSummary.womenCounts, boardSummary.menCounts],
  );
  const boardBarChartData = useMemo(() => {
    const labels = Array.isArray(boardSummary.quarterLabels)
      ? [...boardSummary.quarterLabels]
      : [];
    const women = Array.isArray(womenBoardSeries.women)
      ? [...womenBoardSeries.women]
      : [];
    const men = Array.isArray(womenBoardSeries.men)
      ? [...womenBoardSeries.men]
      : [];

    return { labels, women, men };
  }, [
    boardSummary.quarterLabels,
    womenBoardSeries.women,
    womenBoardSeries.men,
  ]);
  console.log(womenBoardSeries, "boardSummary");
  const boardLineChartSeries = useMemo(() => {
    const labels = Array.isArray(boardSummary?.quarterLabels)
      ? [...boardSummary.quarterLabels]
      : [];
    const data = Array.isArray(boardSummary?.totalMemberTrend)
      ? [...boardSummary.totalMemberTrend]
      : [];

    // With a single point, Chart.js doesn't draw a visible line segment.
    // Prepend a zero-start point so the curve starts from 0 and rises to the value.
    if (labels.length === 1 && data.length === 1) {
      labels.unshift("");
      data.unshift(0);
    }

    return { labels, data };
  }, [boardSummary?.quarterLabels, boardSummary?.totalMemberTrend]);
  const totalBoardMembersDatasets = [
    {
      label: "Board Members",
      data: boardLineChartSeries.data,
      borderColor: "#0D6EFD",
      backgroundColor: "rgba(13, 110, 253, 0.1)",
      borderWidth: 3,
      fill: false,
      tension: 0.4,
      pointRadius: 0,
    },
  ];
  const riskXAxis = quarterTimelineLabels;
  const riskYAxis = [
    "Decarbonization\n& CBAM",
    "global overcapacity\n& Dumping",
    "Energy and raw\nmaterial Volatility",
  ];

  const riskHeatmapData = [
    [0, 0, -4],
    [1, 0, -5],
    [2, 0, -6],
    [3, 0, -7],
    [0, 1, -8],
    [1, 1, -7],
    [2, 1, -5],
    [3, 1, -4],
    [0, 2, -3],
    [1, 2, 2],
    [2, 2, -1],
    [3, 2, -5],
  ];

  const riskColorByValue = {
    "-8": "#DD505A",
    "-7": "#DD505A",
    "-6": "#E27E85",
    "-5": "#E27E85",
    "-4": "#E6989D",
    "-3": "#E6989D",
    "-2": "#EFDBDD",
    "-1": "#EFDBDD",
    0: "#EFDBDD",
    1: "#EFDBDD",
    2: "#C1D8C2",
  };

  const riskMatrixRows = useMemo(
    () =>
      riskYAxis.map((riskLabel, rowIndex) =>
        riskXAxis.map((quarterLabel, colIndex) => {
          const point = riskHeatmapData.find(
            ([x, y]) => x === colIndex && y === rowIndex,
          );
          const score = point ? point[2] : 0;
          return {
            id: `${riskLabel}-${quarterLabel}`,
            score,
            color:
              riskColorByValue[String(score)] ??
              (score > 2 ? "#C1D8C2" : "#EFDBDD"),
          };
        }),
      ),
    [riskXAxis, riskYAxis, riskHeatmapData],
  );

  const riskInsightSummary = useMemo(() => {
    const byQueryPayload = governanceData?.byQuery || {};
    const quarters = Array.isArray(byQueryPayload?.quarters)
      ? byQueryPayload.quarters
      : [];

    const quarterToNumber = (q) => {
      const raw = String(q ?? "").trim();
      if (!raw) return null;
      const asNum = Number(raw);
      if (asNum >= 1 && asNum <= 4) return asNum;
      const normalized = raw.toLowerCase().replace(/\s+/g, "");
      if (normalized.startsWith("jan-mar") || normalized === "q1") return 1;
      if (normalized.startsWith("apr-jun") || normalized === "q2") return 2;
      if (normalized.startsWith("jul-sep") || normalized === "q3") return 3;
      if (normalized.startsWith("oct-dec") || normalized === "q4") return 4;
      return null;
    };

    const normalizedQuarters = quarters
      .map((row) => {
        const year = Number(
          row?.year ??
            row?.startyear ??
            row?.startYear ??
            row?.endyear ??
            row?.endYear,
        );
        const quarterRaw =
          row?.quarter ??
          row?.startquarter ??
          row?.startQuarter ??
          row?.endquarter ??
          row?.endQuarter;
        const quarterNum = quarterToNumber(quarterRaw);
        const data = row?.data || row || {};
        return { year, quarterNum, data };
      })
      .filter((r) => r.year && r.quarterNum)
      .sort((a, b) => a.year - b.year || a.quarterNum - b.quarterNum);

    const latest = normalizedQuarters[normalizedQuarters.length - 1];
    const issues =
      latest?.data?.riskManagement?.materialIssuesIdentified ||
      latest?.riskManagement?.materialIssuesIdentified ||
      [];

    const normalizedIssues = (Array.isArray(issues) ? issues : [])
      .map((item) => ({
        name: item?.materialIssueIdentified || "",
        type: String(item?.riskOrOpportunity || "").toLowerCase(),
        implication: String(item?.financialImplications || "").toLowerCase(),
      }))
      .filter((item) => item.name);

    return {
      topRisks: normalizedIssues
        .filter((item) => item.type.includes("risk"))
        .slice(0, 3),
      topOpportunities: normalizedIssues
        .filter((item) => item.type.includes("opportunity"))
        .slice(0, 3),
    };
  }, [governanceData?.byQuery]);

  const insightDotColor = (item) => {
    const type = String(item?.type || "").toLowerCase();
    if (type.includes("risk")) return "#DD505A";
    if (type.includes("opportunity")) return "#2E9E62";
    return "#F39C12";
  };

  const annualPolicyChart = useMemo(() => {
    const byQueryPayload = governanceData?.byQuery || {};
    const annualRows = Array.isArray(byQueryPayload?.annual)
      ? byQueryPayload.annual
      : [];
    const startYear = Number(fromYear);
    const endYear = Number(toYear);

    const policyMeta = [
      {
        key: "businessEthicsPolicy",
        label: "Business Ethics Policy",
        color: "#DE5353",
      },
      {
        key: "environmentalPolicy",
        label: "Environmental Policy",
        color: "#4C92C3",
      },
      {
        key: "supplierCodeOfConductPolicy",
        label: "Supplier Code of Conduct Policy",
        color: "#FF983E",
      },
      {
        key: "sustainableProcurementPolicy",
        label: "Sustainable Procurement Policy",
        color: "#4C92C3",
      },
      {
        key: "antibrieberyAndCorruptionPolicy",
        label: "Antibriebery and Corruption Policy",
        color: "#A985C9",
      },
      {
        key: "policyOnCyberSecurityAndRisksRelatedToDataPrivacy",
        label: "Policy on Cyber Security and Data Privacy",
        color: "#DE5353",
      },
    ];

    const normalized = annualRows
      .map((row) => {
        const data = row?.data || row || {};
        const year =
          Number(row?.year) ||
          Number(data?.year) ||
          Number(data?._governanceDataRef?.yearToSubmit) ||
          (data?.createdAt ? new Date(data.createdAt).getFullYear() : null);
        const policies = data?.policies || row?.policies || {};
        const recordStatus = String(
          data?.status ?? row?.status ?? data?._governanceDataRef?.status ?? "",
        )
          .trim()
          .toLowerCase();
        if (!year) return null;
        return { year, policies, recordStatus };
      })
      .filter(Boolean)
      .sort((a, b) => a.year - b.year);

    // X-axis should always follow selected dropdown year range.
    const yearLabels =
      startYear && endYear && endYear >= startYear
        ? Array.from(
            { length: endYear - startYear + 1 },
            (_, i) => startYear + i,
          )
        : Array.from(new Set(normalized.map((r) => r.year))).sort(
            (a, b) => a - b,
          );

    if (yearLabels.length === 0) {
      return { yearLabels: [Number(toYear)], rows: [] };
    }

    const yearCount = yearLabels.length;
    const hasPolicyCoverage = (status) => {
      if (typeof status === "boolean") return status;
      if (typeof status === "number") return status === 1;
      const normalized = String(status || "")
        .trim()
        .toLowerCase();
      return (
        normalized === "true" || normalized === "yes" || normalized === "1"
      );
    };
    const completedByYear = new Map();
    normalized.forEach((r) => {
      if (r?.recordStatus === "completed") {
        completedByYear.set(r.year, r.policies || {});
      }
    });

    const rows = policyMeta.map((meta) => ({
      label: meta.label,
      values: yearLabels.map((year) =>
        hasPolicyCoverage(completedByYear.get(year)?.[meta.key]?.status)
      ),
    }));

    return { yearLabels, rows, yearCount };
  }, [governanceData?.byQuery, fromYear, toYear]);

  const transparencyCards = [
    {
      title: "Frequency of financial and ESG reporting",
      value:
        fixedPercentage(
          governanceData?.transparency?.frequencyReporting?.monthData,
        ) ?? "N/A",
      // changeValue: ``,
      // change: getChangeType(
      //   governanceData?.transparency?.frequencyReporting?.percentage
      // ),
    },
    {
      title: "Adherence to reporting standards",
      value: governanceData?.transparency?.adheresToReportingStandards
        ? "Yes"
        : "No",
      changeValue: "",
      change: "",
    },
  ];

  const annualExecutiveCards = useMemo(() => {
    const byQueryPayload = governanceData?.byQuery || {};
    const annualRows = Array.isArray(byQueryPayload?.annual)
      ? byQueryPayload.annual
      : [];

    const normalized = annualRows
      .map((row) => {
        const data = row?.data || row || {};
        const year =
          Number(row?.year) ||
          Number(data?.year) ||
          Number(data?._governanceDataRef?.yearToSubmit) ||
          (data?.createdAt ? new Date(data.createdAt).getFullYear() : null);
        return { year, data };
      })
      .filter((r) => r.year)
      .sort((a, b) => a.year - b.year);

    const latest = normalized[normalized.length - 1]?.data || {};
    const previous =
      normalized.length > 1
        ? normalized[normalized.length - 2]?.data || {}
        : null;

    const calcYoY = (current, prev) => {
      const c = Number(current);
      const p = Number(prev);
      if (Number.isNaN(c) || Number.isNaN(p) || p === 0) return null;
      return ((c - p) / p) * 100;
    };
    const parseNullableNumber = (value) => {
      if (value === null || value === undefined) return null;
      const cleaned = String(value).replace("%", "").trim();
      if (!cleaned) return null;
      const num = Number(cleaned);
      return Number.isNaN(num) ? null : num;
    };

    const riskMeetingValues = normalized
      .map((row) =>
        Number(
          row?.data?.riskManagement?.riskManagementNumber ??
            row?.data?.riskManagementNumber,
        ),
      )
      .filter((v) => !Number.isNaN(v));
    const riskMeetingsRaw =
      latest?.riskManagement?.riskManagementNumber ??
      latest?.riskManagementNumber ??
      "0";
    const riskMeetingsAvg =
      riskMeetingValues.length > 0
        ? riskMeetingValues.reduce((sum, v) => sum + v, 0) /
          riskMeetingValues.length
        : Number(riskMeetingsRaw);
    const riskMeetingsValue = Number.isNaN(riskMeetingsAvg)
      ? String(riskMeetingsRaw)
      : String(truncateNumber(riskMeetingsAvg, 2));

    const topExecPct = parseValue(
      latest?.executiveCompensation?.percentageIncreaseTopExec,
    );
    const topExecPctValues = normalized
      .map((row) =>
        parseValue(row?.data?.executiveCompensation?.percentageIncreaseTopExec),
      )
      .filter((v) => !Number.isNaN(v));
    const topExecPctAvg = topExecPctValues.length
      ? topExecPctValues.reduce((sum, v) => sum + v, 0) /
        topExecPctValues.length
      : 0;
    const medianEmpPct = parseValue(
      latest?.executiveCompensation?.medianPercentageIncreaseEmployees,
    );
    const medianEmpPctValues = normalized
      .map((row) =>
        parseValue(
          row?.data?.executiveCompensation?.medianPercentageIncreaseEmployees,
        ),
      )
      .filter((v) => !Number.isNaN(v));
    const medianEmpPctAvg = medianEmpPctValues.length
      ? medianEmpPctValues.reduce((sum, v) => sum + v, 0) /
        medianEmpPctValues.length
      : 0;

    const prevRiskMeetings =
      previous?.riskManagement?.riskManagementNumber ??
      previous?.riskManagementNumber;
    const prevTopExecPct =
      previous?.executiveCompensation?.percentageIncreaseTopExec;
    const prevMedianEmpPct =
      previous?.executiveCompensation?.medianPercentageIncreaseEmployees;
    const latestTopExecPctRaw =
      latest?.executiveCompensation?.percentageIncreaseTopExec;
    const latestMedianEmpPctRaw =
      latest?.executiveCompensation?.medianPercentageIncreaseEmployees;

    return {
      riskMeetingsValue,
      topExecPct,
      topExecPctAvg,
      medianEmpPct,
      medianEmpPctAvg,
      riskMeetingsYoY: calcYoY(riskMeetingsRaw, prevRiskMeetings),
      topExecYoY: calcYoY(
        parseNullableNumber(latestTopExecPctRaw),
        parseNullableNumber(prevTopExecPct),
      ),
      medianEmpYoY: calcYoY(
        parseNullableNumber(latestMedianEmpPctRaw),
        parseNullableNumber(prevMedianEmpPct),
      ),
    };
  }, [governanceData?.byQuery]);

  const annualReportingCards = useMemo(() => {
    const byQueryPayload = governanceData?.byQuery || {};
    const annualRows = Array.isArray(byQueryPayload?.annual) ? byQueryPayload.annual : [];

    const normalized = annualRows
      .map((row) => {
        const data = row?.data || row || {};
        const year =
          Number(row?.year) ||
          Number(data?.year) ||
          Number(data?._governanceDataRef?.yearToSubmit) ||
          (data?.createdAt ? new Date(data.createdAt).getFullYear() : null);
        return { year, data };
      })
      .filter((r) => r.year)
      .sort((a, b) => a.year - b.year);

    const startYear = Number(fromYear);
    const endYear = Number(toYear);
    const rowsInSelectedRange =
      startYear && endYear && endYear >= startYear
        ? normalized.filter((r) => r.year >= startYear && r.year <= endYear)
        : normalized;

    const latest = normalized[normalized.length - 1]?.data || {};
    const previous = normalized.length > 1 ? normalized[normalized.length - 2]?.data || {} : null;

    const readFirst = (source, paths) => {
      for (const path of paths) {
        const value = path.split(".").reduce((acc, key) => acc?.[key], source);
        if (value !== undefined && value !== null && String(value).trim() !== "") return value;
      }
      return null;
    };

    const toNullableNumber = (value) => {
      if (value === null || value === undefined) return null;
      const cleaned = String(value).replace(/,/g, "").replace("%", "").trim();
      if (!cleaned) return null;
      const n = Number(cleaned);
      return Number.isNaN(n) ? null : n;
    };

    const calcYoY = (current, prev) => {
      const c = toNullableNumber(current);
      const p = toNullableNumber(prev);
      if (c === null || p === null || p === 0) return null;
      return ((c - p) / p) * 100;
    };

    const latestFinancial = readFirst(latest, [
      "transparencyAndDisclosure.financialReportingFrequency",
      "transparency.frequencyOfFinancialReporting",
      "transparency.financialReportingFrequency",
      "transparency.frequencyReporting.monthData",
    ]);
    const previousFinancial = readFirst(previous, [
      "transparencyAndDisclosure.financialReportingFrequency",
      "transparency.frequencyOfFinancialReporting",
      "transparency.financialReportingFrequency",
      "transparency.frequencyReporting.monthData",
    ]);

    const latestEsg = readFirst(latest, [
      "transparencyAndDisclosure.hasESGReports.esgReportingFrequency",
      "transparency.frequencyOfEsgReport",
      "transparency.frequencyOfESGReport",
      "transparency.esgReportFrequency",
      "transparency.frequencyReporting.esgData",
    ]);
    const previousEsg = readFirst(previous, [
      "transparencyAndDisclosure.hasESGReports.esgReportingFrequency",
      "transparency.frequencyOfEsgReport",
      "transparency.frequencyOfESGReport",
      "transparency.esgReportFrequency",
      "transparency.frequencyReporting.esgData",
    ]);

    const formatDisplay = (value, fallback = "0") => {
      if (value === null || value === undefined || String(value).trim() === "") return fallback;
      return String(value);
    };
    const averageFromRows = (rows, paths, fallback = "0") => {
      const values = rows
        .map((row) => toNullableNumber(readFirst(row?.data || {}, paths)))
        .filter((v) => v !== null);
      if (values.length === 0) return fallback;
      return String(truncateNumber(values.reduce((sum, v) => sum + v, 0) / values.length, 2));
    };

    const averageFinancialValue = averageFromRows(rowsInSelectedRange, [
      "transparencyAndDisclosure.financialReportingFrequency",
      "transparency.frequencyOfFinancialReporting",
      "transparency.financialReportingFrequency",
      "transparency.frequencyReporting.monthData",
    ]);
    const averageEsgValue = averageFromRows(
      rowsInSelectedRange,
      [
        "transparencyAndDisclosure.hasESGReports.esgReportingFrequency",
        "transparency.frequencyOfEsgReport",
        "transparency.frequencyOfESGReport",
        "transparency.esgReportFrequency",
        "transparency.frequencyReporting.esgData",
      ],
      "0"
    );

    const valueByYear = (rows, paths) => {
      const map = new Map();
      rows.forEach((row) => {
        const v = toNullableNumber(readFirst(row?.data || {}, paths));
        if (v !== null) map.set(Number(row.year), v);
      });
      return map;
    };

    const financialByYear = valueByYear(rowsInSelectedRange, [
      "transparencyAndDisclosure.financialReportingFrequency",
      "transparency.frequencyOfFinancialReporting",
      "transparency.financialReportingFrequency",
      "transparency.frequencyReporting.monthData",
    ]);
    const esgByYear = valueByYear(rowsInSelectedRange, [
      "transparencyAndDisclosure.hasESGReports.esgReportingFrequency",
      "transparency.frequencyOfEsgReport",
      "transparency.frequencyOfESGReport",
      "transparency.esgReportFrequency",
      "transparency.frequencyReporting.esgData",
    ]);

    const thisYear = Number(endYear) || (rowsInSelectedRange[rowsInSelectedRange.length - 1]?.year ?? null);
    const lastYear = thisYear ? thisYear - 1 : null;

    return {
      financialValue: averageFinancialValue,
      thisYearFinancialValue: thisYear ? financialByYear.get(thisYear) ?? null : null,
      lastYearFinancialValue: lastYear ? financialByYear.get(lastYear) ?? null : null,
      previousFinancialValue: formatDisplay(previousFinancial),
      esgValue: averageEsgValue,
      thisYearEsgValue: thisYear ? esgByYear.get(thisYear) ?? null : null,
      lastYearEsgValue: lastYear ? esgByYear.get(lastYear) ?? null : null,
      previousEsgValue: formatDisplay(previousEsg, "0"),
    };
  }, [governanceData?.byQuery, fromYear, toYear]);

  const formatYoYText = (value) => {
    if (value === null || Number.isNaN(Number(value))) return "0% YOY →";
    const numeric = Number(value);
    const arrow = numeric > 0 ? "↗" : numeric < 0 ? "↘" : "→";
    const prefix = numeric > 0 ? "+" : "";
    return `${prefix}${truncateNumber(numeric, 2)}% YOY ${arrow}`;
  };

  const getYoYByFormula = (currentValue, previousValue) => {
    const current = Number(String(currentValue ?? "").replace(/,/g, "").replace("%", "").trim());
    const previous = Number(String(previousValue ?? "").replace(/,/g, "").replace("%", "").trim());
    if (Number.isNaN(current) || Number.isNaN(previous) || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  return (
    <MainDashboardLayout
      sidebar={
        user?.groupManager ? <GroupAdminSidebar /> : <GovernanceLayout />
      }
    >
      <Unauthorized requiredModule="Governance">
        <>
          <p className={styles.heading}>Governance Dashboard</p>
          <div className={styles.heading_container}>
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setViewType("quarterly")}
                  style={{
                    border:
                      viewType === "quarterly"
                        ? "1px solid #1c486b"
                        : "1px solid #D6D6D6",
                    borderRadius: "12px 12px 0 0",
                    width: "104px",
                    height: "40px",
                    padding: "10px 20px",
                    cursor: "pointer",
                    backgroundColor:
                      viewType === "quarterly" ? "#1c486b" : "#F0F0F0",
                    color: viewType === "quarterly" ? "#fff" : "#000",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: 1,
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                    transition: "all 0.2s ease",
                    opacity: 1,
                  }}
                >
                  Quarterly
                </button>
                <button
                  type="button"
                  onClick={() => setViewType("annually")}
                  style={{
                    border:
                      viewType === "annually"
                        ? "1px solid #1c486b"
                        : "1px solid #D6D6D6",
                    borderRadius: "12px 12px 0 0",
                    width: "104px",
                    height: "40px",
                    padding: "10px 20px",
                    cursor: "pointer",
                    backgroundColor:
                      viewType === "annually" ? "#1c486b" : "#F0F0F0",
                    color: viewType === "annually" ? "#fff" : "#000",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: 1,
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                    transition: "all 0.2s ease",
                    opacity: 1,
                  }}
                >
                  Annually
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "14px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <div>From</div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    {viewType === "quarterly" && (
                      <select
                        name="fromQuarter"
                        id="fromQuarter"
                        value={fromQuarter}
                        onChange={(e) => setFromQuarter(e.target.value)}
                        className={styles.heading_select_month}
                      >
                        {fromQuarterOptions.map((m) => (
                          <option
                            key={m?.id || m?.month}
                            value={m?.value || m?.month}
                          >
                            {`Q${Number(m?.value ?? m?.id ?? 1)}`}
                          </option>
                        ))}
                      </select>
                    )}
                    <select
                      name="fromYear"
                      id="fromYear"
                      value={fromYear}
                      onChange={(e) => setFromYear(e.target.value)}
                      className={styles.heading_select_year}
                    >
                      {fromYearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <span>To</span>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    {viewType === "quarterly" && (
                      <select
                        name="toQuarter"
                        id="toQuarter"
                        value={toQuarter}
                        onChange={(e) => setToQuarter(e.target.value)}
                        className={styles.heading_select_month}
                      >
                        {toQuarterOptions.map((m) => (
                          <option
                            key={m?.id || m?.month}
                            value={m?.value || m?.month}
                          >
                            {`Q${Number(m?.value ?? m?.id ?? 1)}`}
                          </option>
                        ))}
                      </select>
                    )}
                    <select
                      name="toYear"
                      id="toYear"
                      value={toYear}
                      onChange={(e) => setToYear(e.target.value)}
                      className={styles.heading_select_year}
                    >
                      {toYearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!hasLoadedOnce && isLoading ? (
            <div className={styles.dashboard_loader}>
              <CircularProgress size={36} />
              <p className={styles.dashboard_loader_text}>Loading dashboard…</p>
            </div>
          ) : (
            viewType === "quarterly" && (
              <>
                <div className={styles.cards_container_one}>
                  <p className={styles.heading_sub}>
                    Board Structure and Composition
                  </p>
                  <div className={styles.board_dashboard_grid}>
                    <div className={styles.board_analytics_card}>
                      <p className={styles.board_card_title}>
                        Board Members{" "}
                        {/* <span className={styles.board_card_value_big}>
                        {formatTwoDigitsIfDecimal(boardSummary.totalBoardMembers || 0)}
                      </span> */}
                      </p>
                      <div
                        className={[
                          styles.board_chart_wrapper,
                          styles.board_chart_wrapper_compact,
                        ].join(" ")}
                      >
                        <LineChart
                          dataset={totalBoardMembersDatasets}
                          labels={boardLineChartSeries.labels}
                          yAxisTitle="Total Number of Board Members"
                          customHeight="260px"
                          display={false}
                          marginTop="2rem"
                          graphxasis={3}
                          tooltipDisplayColors={false}
                          singlePointFromOrigin={
                            boardSummary.quarterLabels.length === 1
                          }
                        />
                      </div>
                    </div>

                    <div className={styles.board_analytics_card}>
                      <div className={styles.board_chart_header}>
                        <p className={styles.board_card_title}>
                          Percentage of Women on Board:{" "}
                          {formatTwoDigitsIfDecimal(
                            boardSummary.womenPercentage || 0,
                          )}
                          %
                        </p>
                      </div>
                      <div
                        className={[
                          styles.board_chart_wrapper,
                          styles.board_chart_wrapper_compact,
                        ].join(" ")}
                      >
                        <div style={{ marginTop: "49px", height: "274px" }}>
                          <Bar
                            data={{
                              labels: boardBarChartData.labels,
                              datasets: [
                                {
                                  label: "Women",
                                  data: boardBarChartData.women,
                                  backgroundColor: "#0D6EFD",
                                  stack: "total",
                                  categoryPercentage: 0.55,
                                  barPercentage: 0.6,
                                },
                                {
                                  label: "Men",
                                  data: boardBarChartData.men,
                                  backgroundColor: "#F5AA28",
                                  stack: "total",
                                  categoryPercentage: 0.55,
                                  barPercentage: 0.6,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              layout: {
                                padding: {
                                  top: 20,
                                  right: 16,
                                  bottom: 30,
                                  left: 42,
                                },
                              },
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    label: (context) => {
                                      const v = Number(context.raw) || 0;
                                      return `${context.dataset.label}: ${v}`;
                                    },
                                  },
                                },
                                datalabels: { display: false },
                              },
                              scales: {
                                x: {
                                  stacked: true,
                                  grid: { display: false, drawBorder: false },
                                  ticks: {
                                    color: "#2f2f2f",
                                    font: { size: 11 },
                                  },
                                },
                                y: {
                                  stacked: true,
                                  min: 0,
                                  grid: { color: "#e5e5e5", drawBorder: false },
                                  ticks: {
                                    color: "#2f2f2f",
                                    font: { size: 11 },
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                      <div
                        className={`${styles.board_legend} ${styles.board_legend_bottom}`}
                      >
                        <span className={styles.legend_item}>
                          <span
                            className={styles.legend_dot}
                            style={{ backgroundColor: "#F5AA28" }}
                          />
                          Men
                        </span>
                        <span className={styles.legend_item}>
                          <span
                            className={styles.legend_dot}
                            style={{ backgroundColor: "#0D6EFD" }}
                          />
                          Women
                        </span>
                      </div>
                      {/* <div className={styles.dummy_values_row}>
                      {boardSummary.quarterLabels.map((label, index) => (
                        <p key={label} className={styles.dummy_value_item}>
                          {label}: W {womenBoardSeries.women[index]} / M{" "}
                          {womenBoardSeries.men[index]}
                        </p>
                      ))}
                    </div> */}
                    </div>

                    <div className={styles.board_analytics_card}>
                      <p className={styles.board_card_title}>
                        Percentage of Independent Directors on Board
                      </p>
                      <p className={styles.board_metric_value}>
                        {truncateNumber(
                          boardSummary.independentPercentage || 0,
                          2,
                        )}
                        %
                      </p>
                    </div>

                    <div className={styles.board_analytics_card}>
                      <p className={styles.board_card_title}>
                        Average Tenure of Board Members
                      </p>
                      <p className={styles.board_metric_value}>
                        {truncateToTwoDecimals(boardSummary.averageTenure || 0)}{" "}
                        years
                      </p>
                    </div>
                  </div>
                </div>

                <div className={styles.risk_section}>
                  <p className={styles.heading_sub}>Risk Management</p>
                  {/* <div className={styles.risk_summary_card}>
                  <p className={styles.risk_summary_title}>
                    Frequency of Risk Assessments:
                  </p>
                  <div className={styles.risk_summary_bottom}>
                    <p className={styles.risk_summary_value}>
                      {governanceData?.risk?.data
                    ? governanceData?.risk?.data
                        : "N/A"}
                    </p>
                    <p className={styles.risk_summary_change}>-50% QOQ</p>
            </div>
          </div> */}

                  <div className={styles.risk_heatmap_grid}>
                    <div className={styles.risk_heatmap_card}>
                      <p className={styles.risk_heatmap_title}>
                        Top 3 identified Risk
                      </p>
                      <div className={styles.risk_insight_list}>
                        {(riskInsightSummary.topRisks.length
                          ? riskInsightSummary.topRisks
                          : [{ name: "No risk data", implication: "neutral" }]
                        ).map((item) => (
                          <div
                            key={`risk-${item.name}`}
                            className={styles.risk_insight_item}
                          >
                            <span
                              className={styles.risk_insight_dot}
                              style={{ backgroundColor: insightDotColor(item) }}
                            />
                            <span className={styles.risk_insight_name}>
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* <div className={styles.custom_heatmap_container}>
                      <div className={styles.custom_heatmap_y_axis_title}>
                        Key Industry Risks
                      </div>
                      <div className={styles.custom_heatmap_content}>
                        <div className={styles.custom_heatmap_rows}>
                          {riskYAxis.map((riskLabel, rowIndex) => (
                            <div
                              key={riskLabel}
                              className={styles.custom_heatmap_row}
                            >
                              <p className={styles.custom_heatmap_y_label}>
                                {riskLabel}
                              </p>
                              <div className={styles.custom_heatmap_cells}>
                                {riskMatrixRows[rowIndex].map((cell) => (
                                  <div
                                    key={cell.id}
                                    className={styles.custom_heatmap_cell}
                                    style={{ backgroundColor: cell.color }}
                                    title={`Score: ${cell.score}`}
                                  >
                                    {cell.score}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className={styles.custom_heatmap_x_axis}>
                          <span className={styles.custom_heatmap_x_axis_spacer} />
                          <div className={styles.custom_heatmap_x_labels}>
                            {riskXAxis.map((xLabel) => (
                              <p
                                key={xLabel}
                                className={styles.custom_heatmap_x_label}
                              >
                                {xLabel}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className={styles.custom_heatmap_legend}>
                        <div className={styles.custom_heatmap_legend_gradient} />
                        <div className={styles.custom_heatmap_legend_labels}>
                          <span>8</span>
                          <span>0</span>
                          <span>2</span>
                        </div>
                      </div>
                    </div> */}
                    </div>

                    <div className={styles.risk_heatmap_card}>
                      <p className={styles.risk_heatmap_title}>
                        Top 3 identified Opportunities
                      </p>
                      <div className={styles.risk_insight_list}>
                        {(riskInsightSummary.topOpportunities.length
                          ? riskInsightSummary.topOpportunities
                          : [
                              {
                                name: "No opportunity data",
                                implication: "neutral",
                              },
                            ]
                        ).map((item) => (
                          <div
                            key={`opp-${item.name}`}
                            className={styles.risk_insight_item}
                          >
                            <span
                              className={styles.risk_insight_dot}
                              style={{ backgroundColor: insightDotColor(item) }}
                            />
                            <span className={styles.risk_insight_name}>
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* <div className={styles.custom_heatmap_container}>
                      <div className={styles.custom_heatmap_y_axis_title}>
                        Key Industry Risks
                      </div>
                      <div className={styles.custom_heatmap_content}>
                        <div className={styles.custom_heatmap_rows}>
                          {riskYAxis.map((riskLabel, rowIndex) => (
                            <div
                              key={riskLabel}
                              className={styles.custom_heatmap_row}
                            >
                              <p className={styles.custom_heatmap_y_label}>
                                {riskLabel}
                              </p>
                              <div className={styles.custom_heatmap_cells}>
                                {riskMatrixRows[rowIndex].map((cell) => (
                                  <div
                                    key={cell.id}
                                    className={styles.custom_heatmap_cell}
                                    style={{ backgroundColor: cell.color }}
                                    title={`Score: ${cell.score}`}
                                  >
                                    {cell.score}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className={styles.custom_heatmap_x_axis}>
                          <span className={styles.custom_heatmap_x_axis_spacer} />
                          <div className={styles.custom_heatmap_x_labels}>
                            {riskXAxis.map((xLabel) => (
                              <p
                                key={xLabel}
                                className={styles.custom_heatmap_x_label}
                              >
                                {xLabel}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className={styles.custom_heatmap_legend}>
                        <div className={styles.custom_heatmap_legend_gradient} />
                        <div className={styles.custom_heatmap_legend_labels}>
                          <span>8</span>
                          <span>0</span>
                          <span>2</span>
                        </div>
                      </div>
                    </div> */}
                    </div>
                  </div>
                </div>

                <div className={styles.quarterly_compliance_section}>
                  <p className={styles.heading_sub}>Compliance and Ethics</p>

                  <div className={styles.quarterly_compliance_summary_card}>
                    <p className={styles.quarterly_compliance_summary_title}>
                      Number of Significant Instances of non-compliance
                    </p>
                    <div className={styles.quarterly_compliance_summary_bottom}>
                      <p className={styles.quarterly_compliance_summary_value}>
                        {truncateNumber(
                          quarterlyComplianceViolationsTotal || 0,
                          2,
                        )}
                      </p>
                      <p className={styles.quarterly_compliance_summary_change}>
                        {quarterlyComplianceViolationsYoY === null
                          ? "0% QOQ →"
                          : `${quarterlyComplianceViolationsYoY > 0 ? "+" : ""}${truncateNumber(
                              quarterlyComplianceViolationsYoY,
                              2,
                            )}% QOQ ${
                              quarterlyComplianceViolationsYoY > 0
                                ? "↗"
                                : quarterlyComplianceViolationsYoY < 0
                                  ? "↘"
                                  : "→"
                            }`}
                      </p>
                    </div>
                  </div>

                  <div className={styles.quarterly_compliance_chart_card}>
                    <div
                      className={styles.quarterly_compliance_chart_header}
                      style={{ marginBottom: "1.1rem" }}
                    >
                      <p className={styles.quarterly_compliance_chart_title}>
                        Total Fine Paid
                      </p>
                      <p className={styles.quarterly_compliance_chart_value}>
                        {`INR ${(
                          Number(quarterlyFinePaidSeries.total) || 0
                        ).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })} Lakhs`}
                      </p>
                    </div>

                    <div className={styles.compliance_bar_chart_compact}>
                      <p className={styles.compliance_bar_y_axis_label}>
                        Quarter
                      </p>
                      <div style={{ height: 292 }}>
                        <Bar
                          data={{
                            labels: quarterlyFinePaidDisplaySeries.labels,
                            datasets: [
                              {
                                data: quarterlyFinePaidDisplaySeries.values,
                                backgroundColor: "#3E79E0",
                                borderColor: "#3E79E0",
                                borderWidth: 0,
                                borderRadius: 0,
                                categoryPercentage: 0.82,
                                barPercentage: 0.92,
                                maxBarThickness: 46,
                              },
                            ],
                          }}
                          options={{
                            indexAxis: "y",
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: {
                              padding: {
                                top: 8,
                                right: 10,
                                bottom: 40,
                                left: 48,
                              },
                            },
                            plugins: {
                              legend: { display: false },
                              datalabels: { display: false },
                              tooltip: {
                                displayColors: false,
                                callbacks: {
                                  label: (context) =>
                                    `${formatTwoDigitsIfDecimal(Number(context.raw) || 0)} Lakhs`,
                                },
                              },
                            },
                            scales: {
                              y: {
                                ticks: {
                                  color: "#2f2f2f",
                                  font: { size: 12 },
                                },
                                grid: { display: false, drawBorder: false },
                              },
                              x: {
                                min: 0,
                                max: quarterlyFineXAxisConfig.max,
                                ticks: {
                                  color: "#2f2f2f",
                                  font: { size: 12 },
                                  stepSize: quarterlyFineXAxisConfig.stepSize,
                                  callback: (value) =>
                                    Number(value) === 0 ? "" : value,
                                },
                                title: {
                                  display: true,
                                  text: "Fine Amount (Lakhs INR)",
                                  color: "#2f2f2f",
                                  font: { size: 12, weight: "normal" },
                                  padding: { top: 14 },
                                },
                                grid: { display: false, drawBorder: false },
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.shareholder_section}>
                  <p className={styles.heading_sub}>Shareholder Rights</p>
                  <div className={styles.shareholder_card}>
                    <p className={styles.shareholder_card_title}>
                      Shareholder Voting Participation Rate
                    </p>
                    <div className={styles.shareholder_card_bottom}>
                      <p className={styles.shareholder_card_value}>
                        {shareholderVotingSummary.value === null
                          ? "0"
                          : `${truncateNumber(shareholderVotingSummary.value, 2)}%`}
                      </p>
                      <p className={styles.shareholder_card_change}>
                        {shareholderVotingSummary.changePct === null
                          ? "0% QOQ →"
                          : `${shareholderVotingSummary.changePct > 0 ? "+" : ""}${truncateNumber(
                              shareholderVotingSummary.changePct,
                              2,
                            )}% QOQ ${
                              shareholderVotingSummary.changePct > 0
                                ? "↗"
                                : shareholderVotingSummary.changePct < 0
                                  ? "↘"
                                  : "→"
                            }`}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )
          )}

          <div className={styles.cards_container_two}></div>

          {viewType === "annually" && (
            <div className={styles.annual_policy_section}>
              <p className={styles.heading_sub}>Additional</p>
              {/* <div className={styles.annual_policy_card}> */}
                <div className={styles.annual_policy_table_wrap}>
                  <table className={styles.annual_policy_table}>
                    <thead>
                      <tr>
                        <th
                          colSpan={Math.max(annualPolicyChart.yearLabels.length + 1, 1)}
                          className={styles.annual_policy_table_title_row}
                        >
                          Policy Coverage %
                        </th>
                      </tr>
                      <tr>
                        <th>Policies</th>
                        {annualPolicyChart.yearLabels.map((year) => (
                          <th key={year}>{year}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {annualPolicyChart.rows.map((item) => (
                        <tr key={item.label}>
                          <td className={styles.annual_policy_name}>{item.label}</td>
                          {item.values.map((isCovered, idx) => (
                            <td key={`${item.label}-${annualPolicyChart.yearLabels[idx]}`}>
                              <img
                                src={isCovered ? GovernanceTickIcon : GovernanceCrossIcon}
                                alt={isCovered ? "Covered" : "Not Covered"}
                                className={styles.annual_policy_status_icon}
                                style={{ width: isCovered ? "17.47px" : "12.42px" }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              {/* </div> */}
            </div>
          )}

          {viewType === "annually" && (
            <div className={styles.annual_three_cards_section}>
              <div className={styles.annual_three_cards_grid}>
                <div className={styles.annual_three_cards_item}>
                  <p className={styles.heading_sub}>
                    Number of Risk Management Committee Meetings conducted
                  </p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Number of Risk Management Committee Meetings conducted
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {annualExecutiveCards.riskMeetingsValue ? annualExecutiveCards.riskMeetingsValue : "0"}
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {annualExecutiveCards.riskMeetingsYoY === null
                          ? "0% YOY →"
                          : formatYoYText(annualExecutiveCards.riskMeetingsYoY)}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={[
                    styles.annual_three_cards_item,
                    styles.annual_three_cards_item_with_bottom_margin,
                  ].join(" ")}
                >
                  <p className={styles.heading_sub}>
                    Percentage increase in top executive compensation
                  </p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Percentage increase in top executive compensation
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {truncateNumber(
                          annualExecutiveCards.topExecPctAvg || 0,
                          2,
                        )}
                        %
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {formatYoYText(annualExecutiveCards.topExecYoY)}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={[
                    styles.annual_three_cards_item,
                    styles.annual_three_cards_item_gap,
                  ].join(" ")}
                >
                  <p className={styles.heading_sub}>Frequency of Financial reporting</p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Frequency of Financial reporting
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {annualReportingCards.financialValue}
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {formatYoYText(
                          getYoYByFormula(
                            annualReportingCards.thisYearFinancialValue,
                            annualReportingCards.lastYearFinancialValue
                          )
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={[
                    styles.annual_three_cards_item,
                    styles.annual_three_cards_item_gap,
                  ].join(" ")}
                >
                  <p className={styles.heading_sub}>Frequency of ESG Report</p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Frequency of ESG Report
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {annualReportingCards.esgValue}
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {formatYoYText(
                          getYoYByFormula(
                            annualReportingCards.thisYearEsgValue,
                            annualReportingCards.lastYearEsgValue
                          )
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={[
                    styles.annual_three_cards_item,
                    styles.annual_three_cards_item_gap,
                  ].join(" ")}
                >
                  <p className={styles.heading_sub}>
                    Frequency of Financial reporting
                  </p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Frequency of Financial reporting
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {annualReportingCards.financialValue}
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {formatYoYText(
                          getYoYByFormula(
                            annualReportingCards.thisYearFinancialValue,
                            annualReportingCards.lastYearFinancialValue,
                          ),
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={[
                    styles.annual_three_cards_item,
                    styles.annual_three_cards_item_gap,
                  ].join(" ")}
                >
                  <p className={styles.heading_sub}>Frequency of ESG Report</p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Frequency of ESG Report
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {annualReportingCards.esgValue}
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {formatYoYText(
                          getYoYByFormula(
                            annualReportingCards.thisYearEsgValue,
                            annualReportingCards.lastYearEsgValue,
                          ),
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={[
                    styles.annual_three_cards_item,
                    styles.annual_three_cards_item_gap,
                  ].join(" ")}
                >
                  <p className={styles.heading_sub}>
                    Median percentage increase in annual compensation of
                    employees, excluding the highest earner
                  </p>
                  <div className={styles.annual_three_cards_card}>
                    <p className={styles.annual_three_cards_title}>
                      Median percentage increase
                    </p>
                    <div className={styles.annual_three_cards_bottom}>
                      <p className={styles.annual_three_cards_value}>
                        {truncateNumber(
                          annualExecutiveCards.medianEmpPctAvg || 0,
                          2,
                        )}
                        %
                      </p>
                      <p className={styles.annual_three_cards_change}>
                        {annualExecutiveCards.medianEmpYoY === null
                          ? "0% YOY →"
                          : formatYoYText(annualExecutiveCards.medianEmpYoY)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default Dashboard;
